import { Controller } from "@hotwired/stimulus";

export default class DobController extends Controller {
  static targets = ["day", "month", "year", "agePolicy", "underThirteenPolicy", "agePolicyCheckbox", "underThirteenPolicyCheckbox"];

  connect() {
    this.updateDays();
    this.updateAgePolicies();
  }

  updateDays() {
    const year = Number.parseInt(this.yearTarget.value, 10);
    const month = Number.parseInt(this.monthTarget.value, 10);
    const today = new Date();

    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1;
    const currentDay = today.getDate();

    this.resetMonthOptions();

    if (year === currentYear) {
      const isInvalidMonth = this.updateCurrentYearMonths(
        month,
        currentMonth
      );

      if (isInvalidMonth) {
        this.monthTarget.value = "";
        this.dayTarget.value = "";
        this.refreshOptions(this.monthTarget);
        this.refreshOptions(this.dayTarget);
        this.updateAgePolicies();
        return;
      }
    }

    this.refreshOptions(this.monthTarget);

    if (!month) {
      this.dayTarget.value = "";
      for (const option of Array.from(this.dayTarget.options)) {
        option.disabled = false;
      }
      this.refreshOptions(this.dayTarget);
      this.updateAgePolicies();
      return;
    }

    // Allow February 29 until the year is known, then apply leap-year rules.
    const maxDays = new Date(year || 2000, month, 0).getDate();

    this.updateDayOptions(
      year,
      month,
      maxDays,
      currentYear,
      currentMonth,
      currentDay
    );

    this.clearInvalidSelectedDay(
      year,
      month,
      maxDays,
      currentYear,
      currentMonth,
      currentDay
    );

    this.refreshOptions(this.dayTarget);
    this.updateAgePolicies();
  }

  refreshOptions(select) {
    for (const option of Array.from(select.options)) {
      // Fresh options must not retain Select2's cached ID or element reference.
      const replacement = new Option(
        option.text, option.value, option.defaultSelected, option.selected
      );
      replacement.disabled = option.disabled;
      option.replaceWith(replacement);
    }
    $(select).trigger("change.select2");
  }

  resetMonthOptions() {
    for (const option of Array.from(this.monthTarget.options)) {
      option.disabled = false;
    }
  }

  updateCurrentYearMonths(month, currentMonth) {
    for (const option of Array.from(this.monthTarget.options)) {
      const value = Number.parseInt(option.value, 10);

      if (!Number.isNaN(value) && value > currentMonth) {
        option.disabled = true;
      }
    }

    return month > currentMonth;
  }

  updateDayOptions(
    year,
    month,
    maxDays,
    currentYear,
    currentMonth,
    currentDay
  ) {
    for (const option of Array.from(this.dayTarget.options)) {
      const value = Number.parseInt(option.value, 10);

      if (Number.isNaN(value)) {
        continue;
      }

      option.disabled = this.isDayDisabled(
        value,
        year,
        month,
        maxDays,
        currentYear,
        currentMonth,
        currentDay
      );
    }
  }

  isDayDisabled(
    value,
    year,
    month,
    maxDays,
    currentYear,
    currentMonth,
    currentDay
  ) {
    if (value > maxDays) {
      return true;
    }

    return (
      year === currentYear &&
      month === currentMonth &&
      value > currentDay
    );
  }

  clearInvalidSelectedDay(
    year,
    month,
    maxDays,
    currentYear,
    currentMonth,
    currentDay
  ) {
    const selectedDay = Number.parseInt(this.dayTarget.value, 10);

    const isInvalid =
      selectedDay > maxDays ||
      (year === currentYear &&
        month === currentMonth &&
        selectedDay > currentDay);

    if (!isInvalid) {
      return;
    }

    this.dayTarget.value = "";
    $(this.dayTarget).trigger("change");
  }

  updateAgePolicies() {
    if (!this.hasAgePolicyTarget || !this.hasUnderThirteenPolicyTarget) {
      return;
    }

    // Clear previous validation errors
    for (const label of this.element.querySelectorAll("label.error")) label.remove();

    this.agePolicyCheckboxTarget.classList.remove("error");
    this.underThirteenPolicyCheckboxTarget.classList.remove("error");

    const year = Number.parseInt(this.yearTarget.value, 10);
    const month = Number.parseInt(this.monthTarget.value, 10);
    const day = Number.parseInt(this.dayTarget.value, 10);

    // Hide both until complete DOB is selected
    if (!year || !month || !day) {
      this.hidePolicies();
      return;
    }

    const today = new Date();
    let age = today.getFullYear() - year;

    const birthdayHasNotOccurred =
      today.getMonth() + 1 < month ||
      (today.getMonth() + 1 === month && today.getDate() < day);

    if (birthdayHasNotOccurred) {
      age--;
    }

    if (age < 13) {
      // Show under-13 policy
      this.underThirteenPolicyTarget.classList.remove("d-none");
      this.agePolicyTarget.classList.add("d-none");

      this.underThirteenPolicyCheckboxTarget.required = true;
      this.agePolicyCheckboxTarget.required = false;
      this.agePolicyCheckboxTarget.checked = false;
    } else if (age <= 17) {
      // Show 13-17 policy
      this.agePolicyTarget.classList.remove("d-none");
      this.underThirteenPolicyTarget.classList.add("d-none");

      this.agePolicyCheckboxTarget.required = true;
      this.underThirteenPolicyCheckboxTarget.required = false;
      this.underThirteenPolicyCheckboxTarget.checked = false;
    } else {
      // 18+
      this.hidePolicies();
      return;
    }
    // Re-run your existing form validation/button-state logic
    $(this.agePolicyCheckboxTarget).triggerHandler("click");
  }

  hidePolicies() {
    this.agePolicyTarget.classList.add("d-none");
    this.underThirteenPolicyTarget.classList.add("d-none");

    this.agePolicyCheckboxTarget.required = false;
    this.underThirteenPolicyCheckboxTarget.required = false;

    this.agePolicyCheckboxTarget.checked = false;
    this.underThirteenPolicyCheckboxTarget.checked = false;
    this.agePolicyCheckboxTarget.classList.remove("error");
    this.underThirteenPolicyCheckboxTarget.classList.remove("error");

    for (const label of this.element.querySelectorAll("label.error")) label.remove();
  }
}
